/**
 * Outlook/Horizon helper functions
 * Pure functions for parsing and converting RWL codes
 */

/**
 * Converts an RWL code to display value
 * Extracts numeric part and formats it: RWL15 → +1.5°C, RWL27 → +2.7°C
 * @param {string} code - RWL code (e.g., 'RWL15', 'RWL20', 'RWL27')
 * @returns {string|null} Formatted value (e.g., '+1.5°C') or null if invalid
 */
export const convertRwlCode = (code) => {
  if (typeof code !== 'string') return null;

  // Extract numeric part (remove 'RWL' or any non-numeric prefix)
  const numericPart = code.replace(/^\D+/, '');

  if (!numericPart || Number.isNaN(Number(numericPart))) return null;

  // Convert to number and format
  const value = Number(numericPart);

  // Divide by 10 to get decimal value (15 → 1.5, 27 → 2.7, 30 → 3)
  const temperature = value / 10;

  // Format with one or two decimal places depending on the case
  const formatted = temperature % 1 === 0
    ? temperature.toFixed(0)
    : temperature.toFixed(1);

  return `+${formatted}°C`;
};

/**
 * Parses and converts serialized horizon values
 * Input format: "['RWL15', 'RWL20', 'RWL30']" or ['RWL15', 'RWL20', 'RWL30']
 * @param {string|Array<string>} horizon - Raw horizon data
 * @returns {Array<string>|null} Array of converted RWL values or null
 */
export const parseHorizon = (horizon) => {
  if (!horizon) return null;

  let horizonArray;

  // If it's a string, parse the JSON/array
  if (typeof horizon === 'string') {
    try {
      // Replace single quotes with double quotes for JSON.parse
      const jsonString = horizon.replaceAll("'", '"');
      horizonArray = JSON.parse(jsonString);
    } catch {
      // If parsing fails, return null
      return null;
    }
  } else if (Array.isArray(horizon)) {
    horizonArray = horizon;
  } else {
    return null;
  }

  // Convert RWL codes to display values
  const convertedValues = horizonArray
    .map((code) => convertRwlCode(code))
    .filter(Boolean);

  return convertedValues.length > 0 ? convertedValues : null;
};
